import {
  Stack,
  TextField,
  useAutocomplete,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Typography,
  Tooltip,
} from '@mui/material'
import { union } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import CollapsibleFilter, { ICollapsibleFilterProps } from '../BaseFilter/CollapsibleFilter'

export interface IOption {
  description?: string
  endAdornment?: React.ReactNode
  id: string
  label: string
  value?: number
}

interface IOptionsProps {
  checkItem: (id: string, status: boolean) => void
  options: IOption[]
  values: string[]
}

const Options = function (props: IOptionsProps) {
  return (
    <>
      <List>
        {props.options.map((option) => {
          const labelId = `label-${option.id}`
          const checked = props.values.includes(option.id)

          return (
            <Tooltip
              title={option.description && <Typography variant={'caption'}>{option.description}</Typography>}
              key={option.id}
              followCursor
              enterDelay={250}
              enterNextDelay={500}
            >
              <ListItem
                key={option.id}
                secondaryAction={
                  (option.value || option.endAdornment) && (
                    <Stack direction={'row'} alignItems={'center'} columnGap={'1ch'}>
                      <Typography variant={'subtitle1'}>{option.value}</Typography>
                      {option.endAdornment}
                    </Stack>
                  )
                }
                sx={{
                  paddingTop: 0,
                  paddingBottom: '0.5em',
                  '& > .MuiListItemButton-root': {
                    paddingRight: 0,
                  },
                }}
              >
                <ListItemButton
                  role={undefined}
                  onClick={() => props.checkItem(option.id, !checked)}
                  sx={{ padding: 0 }}
                  id={option.id}
                >
                  <ListItemIcon sx={{ minWidth: 'min-content' }}>
                    <Checkbox
                      edge={'start'}
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                      sx={{ paddingY: 0, '& .MuiSvgIcon-root': { fontSize: '2.3em' } }}
                      // Typing is not exact for CheckboxListFilter but option.id could be a number
                      // and id only accept a string
                      id={option.id.toString()}
                    />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'h6', id: labelId }}>{option.label}</ListItemText>
                </ListItemButton>
              </ListItem>
            </Tooltip>
          )
        })}
      </List>
    </>
  )
}

const Autocomplete = function (props: IOptionsProps) {
  const { t } = useTranslation()

  const { getRootProps, getInputProps, groupedOptions } = useAutocomplete({
    options: props.options,
    open: true,
    freeSolo: true,
    multiple: true,
  })

  return (
    <Stack flexDirection="column" gap={2}>
      <div {...getRootProps()}>
        <TextField
          inputProps={{ ...getInputProps() }}
          placeholder={t('Search')}
          fullWidth={true}
          variant="standard"
          color="primary"
          size="medium"
        />
      </div>
      <Options options={groupedOptions as any[]} values={props.values} checkItem={props.checkItem} />
    </Stack>
  )
}

export interface ICheckboxListFilterProps extends Omit<ICollapsibleFilterProps<string>, 'items'> {
  autocomplete?: boolean
  items: IOption[]
  nullable?: boolean
  onFilterChange?: (filterId: string, values: string[]) => void
  unique?: boolean
}

function CheckboxListFilter(props: ICheckboxListFilterProps) {
  const canDisplayCheckbox = props.nullable || !props.unique

  const values = props.values ?? []
  const setValues = function (newValues: string[]) {
    props.onFilterChange?.(props.filterId, newValues)
  }

  const selectAll = function () {
    setValues(props.items.map((item) => item.id))
  }

  const unselectAll = function () {
    setValues([])
  }

  const handleCheckItem = (value: string, checked: boolean) => {
    if (checked) {
      if (props.unique) {
        setValues([value])
      } else {
        setValues(union(props.values, [value]))
      }
    } else if (props.unique && !props.nullable) {
      setValues([value])
    } else {
      setValues(values.filter((vId) => vId !== value))
    }
  }

  const ItemsComponent = props.autocomplete ? Autocomplete : Options

  return (
    <CollapsibleFilter
      {...props}
      values={values}
      displayCheckbox={canDisplayCheckbox && Boolean(props.displayCheckbox)}
      selectAll={selectAll}
      unselectAll={unselectAll}
    >
      <ItemsComponent options={props.items} values={values} checkItem={handleCheckItem} />
    </CollapsibleFilter>
  )
}

export default CheckboxListFilter
